import axios from "axios";
import { getPortalAccessToken } from "@/authConfig";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

// Attach the Entra access token to user-portal endpoints only. Admin calls set
// their own Authorization header (adminHeaders) and are skipped. /availability
// and /vehicles stay public/unchanged (shared with the admin page).
const USER_AUTH_PATHS = [/^\/bookings(\/|$|\?)/, /^\/otp\/(request|verify)$/];
api.interceptors.request.use(async (config) => {
  const url = config.url || "";
  const hasAuth = config.headers?.Authorization || config.headers?.authorization;
  if (!hasAuth && USER_AUTH_PATHS.some((re) => re.test(url))) {
    const token = await getPortalAccessToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const fetchVehicles = async () => {
  const { data } = await api.get("/vehicles");
  return data;
};

export const fetchAvailability = async (bookingDate) => {
  const { data } = await api.get("/availability", {
    params: { booking_date: bookingDate },
  });
  return data;
};

export const createBooking = async (payload) => {
  const { data } = await api.post("/bookings", payload);
  return data;
};

export const fetchAllBookings = async (params = {}) => {
  const { data } = await api.get("/bookings/all", { params });
  return data;
};

export const requestOtp = async (booking_ref, action, turnstile_token) => {
  const { data } = await api.post("/otp/request", { booking_ref, action, turnstile_token });
  return data;
};

export const verifyOtp = async (booking_ref, action, code) => {
  const { data } = await api.post("/otp/verify", { booking_ref, action, code });
  return data;
};

export const userUpdateBooking = async (bookingRef, payload) => {
  const { data } = await api.patch(`/bookings/${bookingRef}/user-update`, payload);
  return data;
};

export const userCancelBooking = async (bookingRef, payload) => {
  const { data } = await api.patch(`/bookings/${bookingRef}/cancel`, payload);
  return data;
};

// ---------- Admin ----------
const ADMIN_TOKEN_KEY = "ivp_admin_token";
export const getAdminToken = () => localStorage.getItem(ADMIN_TOKEN_KEY) || "";
export const setAdminToken = (t) => localStorage.setItem(ADMIN_TOKEN_KEY, t);
export const clearAdminToken = () => localStorage.removeItem(ADMIN_TOKEN_KEY);

const adminHeaders = () => ({ Authorization: `Bearer ${getAdminToken()}` });

export const adminLogin = async (password, turnstile_token) => {
  const { data } = await api.post("/admin/login", { password, turnstile_token });
  return data;
};

export const fetchAdminDashboard = async (params = {}) => {
  const { data } = await api.get("/admin/dashboard", { params, headers: adminHeaders() });
  return data;
};

export const updateAdminBooking = async (bookingRef, payload) => {
  const { data } = await api.patch(`/admin/bookings/${bookingRef}`, payload, {
    headers: adminHeaders(),
  });
  return data;
};

export const fetchServiceRecords = async (params = {}) => {
  const { data } = await api.get("/admin/service-records", { params, headers: adminHeaders() });
  return data;
};

export const createServiceRecord = async (payload) => {
  const { data } = await api.post("/admin/service-records", payload, { headers: adminHeaders() });
  return data;
};

export const updateServiceRecord = async (id, payload) => {
  const { data } = await api.put(`/admin/service-records/${id}`, payload, { headers: adminHeaders() });
  return data;
};

export const fetchMaintenanceSummary = async () => {
  const { data } = await api.get("/vehicles/maintenance-summary");
  return data;
};

export const transferBooking = async (bookingRef, payload) => {
  const { data } = await api.post(`/admin/bookings/${bookingRef}/transfer`, payload, {
    headers: adminHeaders(),
  });
  return data;
};

export const adminCancelBooking = async (bookingRef, payload) => {
  const { data } = await api.patch(`/admin/bookings/${bookingRef}/cancel`, payload, {
    headers: adminHeaders(),
  });
  return data;
};

export const fetchMaintenance = async () => {
  const { data } = await api.get("/admin/maintenance", { headers: adminHeaders() });
  return data;
};

export const createMaintenance = async (payload) => {
  const { data } = await api.post("/admin/maintenance", payload, { headers: adminHeaders() });
  return data;
};

export const updateMaintenance = async (id, payload) => {
  const { data } = await api.patch(`/admin/maintenance/${id}`, payload, { headers: adminHeaders() });
  return data;
};

export const deleteMaintenance = async (id) => {
  const { data } = await api.delete(`/admin/maintenance/${id}`, { headers: adminHeaders() });
  return data;
};

export const fetchAdminVehicles = async () => {
  const { data } = await api.get("/admin/vehicles", { headers: adminHeaders() });
  return data;
};

export const createVehicle = async (payload) => {
  const { data } = await api.post("/admin/vehicles", payload, { headers: adminHeaders() });
  return data;
};

export const updateVehicle = async (id, payload) => {
  const { data } = await api.patch(`/admin/vehicles/${id}`, payload, { headers: adminHeaders() });
  return data;
};

export const deleteVehicle = async (id) => {
  const { data } = await api.delete(`/admin/vehicles/${id}`, { headers: adminHeaders() });
  return data;
};

export const fetchPendingCharging = async () => {
  const { data } = await api.get("/admin/charging/pending", { headers: adminHeaders() });
  return data;
};

export const completeCharging = async (id, battery_percentage) => {
  const { data } = await api.post(`/admin/charging/${id}/complete`, { battery_percentage }, { headers: adminHeaders() });
  return data;
};
